<template>
    <div class="nevs-content">
        <ModalMatchTransaction :show="showMatchModal" :transaction="matchingTransaction"
                               @done="matchModalDone"></ModalMatchTransaction>
        <div class="nevs-content-top-buttons">
            <NevsTableFilters ref="filterPanel" :summary="filterSummary" @clear="clearFilter"
                              @toggle="filtersToggled">
                <NevsTextField v-model="tableData.filters.search"
                               :label="$LANG.Get('fields.search')"></NevsTextField>
                <NevsDateField v-model="tableData.filters.date_from"
                               :label="$LANG.Get('fields.date_from')"></NevsDateField>
                <NevsDateField v-model="tableData.filters.date_to"
                               :label="$LANG.Get('fields.date_to')"></NevsDateField>
                <NevsSelect :nullable="true" v-model="tableData.filters.status" :options="statusOptions"
                            :label="$LANG.Get('fields.status')"
                            @select="filterLabels.status = $event.label"></NevsSelect>
            </NevsTableFilters>
        </div>
        <NevsCard>
            <NevsTable
                :default-sort="tableData.defaultSort"
                :fields="tableData.fields"
                :height="tableData.height"
                :total-records="tableData.totalRecords"
                @reload="reloadTable">
                <tr v-for="(item, key) in tableData.records" :key="key">
                    <td class="nevs-table-cell">{{ item.date_display }}</td>
                    <td class="nevs-table-cell">{{ item.payer_name }}</td>
                    <td class="nevs-table-cell">{{ item.amount_display }}</td>
                    <td class="nevs-table-cell">{{ item.reference }}</td>
                    <td class="nevs-table-cell">{{ item.description }}</td>
                    <td class="nevs-table-cell">
                        <NevsButton v-if="item.matched" class="primary transaction-invoice-button"
                                    :title="$LANG.Get('tooltips.openInNewWindow')"
                                    @click="openInvoiceClick(item)">{{ item.invoice_display }} <i
                            class="fa-solid fa-up-right-from-square"></i></NevsButton>
                    </td>
                    <td class="nevs-table-cell">
                        <span :class="['transaction-status', status(item)]">
                            {{ $LANG.Get('labels.' + status(item)) }}
                        </span>
                    </td>
                    <td class="nevs-table-actions">
                        <NevsActions :width="'200px'" :actions="tableActions" :context="item"></NevsActions>
                    </td>
                </tr>
            </NevsTable>
        </NevsCard>
    </div>
</template>

<script>
import NevsCard from "@/components/nevs/NevsCard.vue";
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsTable from "@/components/nevs/NevsTable.vue";
import NevsSelect from "@/components/nevs/NevsSelect.vue";
import NevsActions from "@/components/nevs/NevsActions.vue";
import NevsDateField from "@/components/nevs/NevsDateField.vue";
import NevsTextField from "@/components/nevs/NevsTextField.vue";
import NevsTableFilters from "@/components/nevs/NevsTableFilters.vue";
import ModalMatchTransaction from "@/components/modals/ModalMatchTransaction.vue";

export default {
    name: "ModuleTransactions",
    components: {
        ModalMatchTransaction, NevsTableFilters, NevsActions, NevsSelect, NevsDateField, NevsTextField,
        NevsTable, NevsCard, NevsButton
    },
    data() {
        return {
            tableData: {
                fields: [
                    {
                        name: 'booking_date',
                        label: this.$LANG.Get('fields.date')
                    },
                    {
                        name: 'payer_name',
                        label: this.$LANG.Get('fields.payer')
                    },
                    {
                        name: 'amount',
                        label: this.$LANG.Get('fields.amount')
                    },
                    {
                        name: 'reference',
                        label: this.$LANG.Get('fields.reference')
                    },
                    {
                        name: 'description',
                        label: this.$LANG.Get('fields.description'),
                        sortable: false
                    },
                    {
                        name: 'invoice',
                        label: this.$LANG.Get('fields.invoice'),
                        sortable: false
                    },
                    {
                        name: 'status',
                        label: this.$LANG.Get('fields.status'),
                        sortable: false
                    },
                    {
                        name: 'actions',
                        width: '200px',
                        label: '',
                        sortable: false
                    }
                ],
                filters: {
                    search: null,
                    date_from: null,
                    date_to: null,
                    status: null
                },
                records: [],
                lastRequest: null,
                totalRecords: 0,
                defaultSort: {
                    field: 'booking_date',
                    descending: true
                },
                filterTimer: null,
                height: '0px'
            },
            tableActions: [
                // Matching is offered on an ignored transaction too, and takes it out of the ignored ones:
                // deciding which invoice it paid is deciding it was worth dealing with after all.
                {
                    label: this.$LANG.Get('tooltips.matchTransaction'),
                    action: (item) => {
                        this.matchClick(item);
                    },
                    visible: (item) => {
                        return !item.matched;
                    }
                },
                {
                    label: this.$LANG.Get('tooltips.unmatchTransaction'),
                    action: (item) => {
                        this.unmatchClick(item);
                    },
                    visible: (item) => {
                        return item.matched;
                    }
                },
                {
                    label: this.$LANG.Get('tooltips.ignoreTransaction'),
                    action: (item) => {
                        this.ignoreClick(item);
                    },
                    visible: (item) => {
                        return !item.matched && !item.ignored;
                    }
                }
            ],
            // Labels of the select filters, for the collapsed filter panel's summary. v-model only carries the
            // value, so the label is captured from NevsSelect's `select` event.
            filterLabels: {
                status: ''
            },
            showMatchModal: false,
            matchingTransaction: null
        }
    },
    watch: {
        'tableData.filters': {
            handler() {
                if (this.filterTimer !== null) {
                    clearTimeout(this.filterTimer);
                }
                let vm = this;
                this.filterTimer = setTimeout(function () {
                    vm.reloadTable(vm.tableData.lastRequest);
                }, 500);
            },
            deep: true
        }
    },
    computed: {
        // Leaving the filter empty lists every transaction; an ignored one is only listed by itself, never
        // among the matched or the unmatched ones.
        statusOptions() {
            return [
                {value: 'matched', label: this.$LANG.Get('labels.matched')},
                {value: 'unmatched', label: this.$LANG.Get('labels.unmatched')},
                {value: 'ignored', label: this.$LANG.Get('labels.ignored')}
            ];
        },
        // Entries whose value is empty are dropped by NevsTableFilters, so every filter can be listed here and
        // the panel picks the active ones.
        filterSummary() {
            let filters = this.tableData.filters;
            return [
                {
                    name: 'search',
                    label: this.$LANG.Get('fields.search'),
                    value: filters.search === null ? '' : filters.search
                },
                {
                    name: 'date_from',
                    label: this.$LANG.Get('fields.date_from'),
                    value: filters.date_from === null ? '' : this.$HELPERS.FormatDate(filters.date_from)
                },
                {
                    name: 'date_to',
                    label: this.$LANG.Get('fields.date_to'),
                    value: filters.date_to === null ? '' : this.$HELPERS.FormatDate(filters.date_to)
                },
                {
                    name: 'status',
                    label: this.$LANG.Get('fields.status'),
                    value: filters.status === null ? '' : this.filterLabels.status
                }
            ];
        }
    },
    methods: {
        // The value each filter has when it is not restricting anything.
        emptyFilters() {
            return {
                search: null,
                date_from: null,
                date_to: null,
                status: null
            };
        },
        // What the row is, as the one word the status cell shows and the status filter names.
        status(transaction) {
            if (transaction.ignored) return 'ignored';
            return transaction.matched ? 'matched' : 'unmatched';
        },
        // Ignoring is for transactions nobody is going to match, so it is offered on unmatched ones only -
        // a matched one is unmatched first. It needs no confirmation: unmatching undoes it.
        ignoreClick(transaction) {
            let vm = this;
            this.$API.APICall('post', 'transactions/ignore/' + transaction.id, {}, (data, success) => {
                if (success) {
                    vm.reloadTable(vm.tableData.lastRequest);
                } else {
                    vm.showError(data);
                }
            });
        },
        // The API answers a refused action with the reason for it, which is a message for the person who
        // asked rather than a server error - anything else is handled as one.
        showError(data) {
            let message = this.$LANG.Get('alerts.transactionErrors.' + data.message);
            if (message.startsWith('alerts.')) {
                this.$HELPERS.HandleServerError(this, data);
                return;
            }
            this.$LOCAL_BUS.TriggerEvent('popup', {text: message, type: 'alert'});
        },
        clearFilter(entry) {
            this.tableData.filters[entry.name] = this.emptyFilters()[entry.name];
            if (this.filterLabels[entry.name] !== undefined) {
                this.filterLabels[entry.name] = '';
            }
        },
        // Expanding the panel makes it taller, so the table gets whatever height is left over.
        filtersToggled() {
            this.$nextTick(() => {
                this.resolveWindowResize();
            });
        },
        // The invoice a matched transaction paid, in a new tab - the grid is where payments are checked
        // against invoices, so opening one should not cost the list and its filters.
        openInvoiceClick(transaction) {
            if (!transaction.matched) return;
            let routeData = this.$router.resolve('/invoices/' + transaction.invoice_id + '/edit');
            window.open(routeData.href);
        },
        matchClick(transaction) {
            this.matchingTransaction = transaction;
            this.showMatchModal = true;
        },
        matchModalDone(success) {
            this.showMatchModal = false;
            this.matchingTransaction = null;
            if (success) {
                this.reloadTable(this.tableData.lastRequest);
            }
        },
        // Unmatching deletes the payment the match created, which is why it is confirmed.
        unmatchClick(transaction) {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.transactionUnmatchQuestion'), callback: (response) => {
                    if (response) {
                        vm.$API.APICall('post', 'transactions/unmatch/' + transaction.id, {}, (data, success) => {
                            if (success) {
                                vm.reloadTable(vm.tableData.lastRequest);
                            } else {
                                vm.showError(data);
                            }
                        });
                    }
                }
            });
        },
        reloadTable(request) {
            let vm = this;
            request.filters = this.tableData.filters;
            this.tableData.lastRequest = JSON.parse(JSON.stringify(request));
            this.$API.APICall('get', 'transactions', request, (data, success) => {
                if (success) {
                    vm.tableData.records = data.records;
                    vm.tableData.totalRecords = data.total_records;
                    vm.$nextTick(() => {
                        vm.resolveWindowResize();
                    });
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        resolveWindowResize() {
            let offset = 205;
            //the filter panel shares the button row, so only its expanded fields add height above the table
            if (this.$refs.filterPanel) {
                offset += this.$refs.filterPanel.fieldsHeight();
            }
            this.tableData.height = window.innerHeight - offset + 'px';
        }
    },
    mounted() {
        this.$store.commit('selectMenu', 'transactions');
        this.$store.commit('selectSubMenu', null);
        this.$store.commit('setBreadcrumbs', [
            {
                label: this.$LANG.Get('modules.transactions'),
                link: null
            }
        ]);
        window.addEventListener('resize', this.resolveWindowResize);
        this.resolveWindowResize();
    }
}
</script>

<style scoped lang="scss">
@use '../../scss/vars' as *;

:deep(.nevs-table-header td) {
    padding-bottom: 10px !important;
}

// Sits in a table row, so it is padded like the status pill next to it rather than like the buttons above
// the grid - a 10px button in every row would make the whole list taller.
.transaction-invoice-button {
    padding: 5px 10px;
    white-space: nowrap;

    i {
        margin-left: 5px;
    }
}

.transaction-status {
    display: inline-block;
    padding: 5px;
    border-radius: 5px;
    // Unmatched is the state that wants attention, so it is the one wearing the warning colour.
    background-color: $banner-warning;
    color: black;
}

.transaction-status.matched {
    background-color: $primary-hover;
    color: inherit;
}

.transaction-status.ignored {
    background-color: $disabled-color;
    color: black;
}
</style>
